// src/App.js - Compact design with BOXED BORDER header
import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Trophy, Volume2, VolumeX, Sun, Moon, Star, Utensils, ChefHat } from 'lucide-react';
import Game from './pages/Game';
import RecipeDirectory from './pages/RecipeDirectory';
import './index.css';

function App() {
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(1);
  const [recipesCooked, setRecipesCooked] = useState(0);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const audioRef = useRef(null);
  const location = useLocation();

  // Load game stats
  useEffect(() => {
    const loadStats = () => {
      const xpData = parseInt(localStorage.getItem('game-points') || '0', 10);
      const levelData = parseInt(localStorage.getItem('game-level') || '1', 10);
      const cookedData = JSON.parse(localStorage.getItem('recipes-cooked') || '[]');
      
      setXp(isNaN(xpData) ? 0 : xpData);
      setLevel(isNaN(levelData) ? 1 : levelData);
      setRecipesCooked(cookedData.length);
    };

    loadStats();

    const handleStorage = () => loadStats();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('gameStatsUpdated', handleStorage);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('gameStatsUpdated', handleStorage);
    };
  }, []);

  // Load theme preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme-preference');
    if (savedTheme === 'light') {
      setIsDarkTheme(false);
      document.documentElement.classList.add('light-theme');
    }
  }, []);

  // Load music preference
  useEffect(() => {
    const savedMusicPref = localStorage.getItem('music-enabled');
    if (savedMusicPref === 'true') {
      setIsMusicPlaying(true);
    }
  }, []);

  // Handle music playback
  useEffect(() => {
    if (audioRef.current) {
      if (isMusicPlaying) {
        audioRef.current.play().catch(err => {
          console.log('Audio play failed:', err);
          setIsMusicPlaying(false);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isMusicPlaying]);

  const toggleMusic = () => {
    const newState = !isMusicPlaying;
    setIsMusicPlaying(newState);
    localStorage.setItem('music-enabled', newState.toString());
  };

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    
    if (newTheme) {
      document.documentElement.classList.remove('light-theme');
      localStorage.setItem('theme-preference', 'dark');
    } else {
      document.documentElement.classList.add('light-theme');
      localStorage.setItem('theme-preference', 'light');
    }
  };

  const xpInLevel = xp % 100;
  const isGamePage = location.pathname === '/';
  const isDirectoryPage = location.pathname === '/directory';

  return (
    <div className="min-h-screen pixel-bg" style={{ background: 'var(--bg)' }}>
      {/* Background Music */}
      <audio 
        ref={audioRef}
        loop
        src="https://cdn.pixabay.com/download/audio/2022/03/10/audio_4a463d5a94.mp3"
      />

      {/* BOXED Header Container */}
      <div 
        className="sticky top-0 z-50"
        style={{ 
          background: 'var(--bg)',
          padding: '1rem'
        }}
      >
        {/* Compact Top Navigation Bar with BORDER BOX */}
        <nav 
          className="border-4 px-6 py-3 max-w-7xl mx-auto"
          style={{ 
            borderColor: 'var(--green)', 
            background: 'var(--bg)',
            boxShadow: '4px 4px 0 rgba(74, 222, 128, 0.2)'
          }}
        >
          <div className="flex items-center justify-between gap-6">
            {/* Left: Logo & Title */}
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 flex items-center justify-center border-2"
                style={{ 
                  background: 'var(--green)', 
                  borderColor: 'var(--green)' 
                }}
              >
                <ChefHat className="w-7 h-7" style={{ color: 'var(--bg)' }} />
              </div>
              <div>
                <h1 className="pixel-text text-xl leading-tight" style={{ color: 'var(--green)' }}>
                  RECIPE QUEST
                </h1>
                <p className="pixel-text text-xs" style={{ color: 'var(--yellow)' }}>
                  COLLECT • COOK • CONQUER
                </p>
              </div>
            </div>

            {/* Center: Navigation Links (on larger screens) */}
            <div className="hidden md:flex gap-3">
              <Link 
                to="/" 
                className="pixel-text text-xs px-3 py-2 border-2 transition-all"
                style={isGamePage ? {
                  background: 'var(--yellow)',
                  color: 'var(--bg)',
                  borderColor: 'var(--yellow)',
                  boxShadow: '0 0 15px rgba(250, 204, 21, 0.3)'
                } : {
                  background: 'transparent',
                  color: 'var(--cyan)',
                  borderColor: 'var(--cyan)'
                }}
              >
                PLAY GAME
              </Link>
              <Link 
                to="/directory" 
                className="pixel-text text-xs px-3 py-2 border-2 transition-all"
                style={isDirectoryPage ? {
                  background: 'var(--cyan)',
                  color: 'var(--bg)',
                  borderColor: 'var(--cyan)',
                  boxShadow: '0 0 15px rgba(34, 211, 238, 0.3)'
                } : {
                  background: 'transparent',
                  color: 'var(--cyan)',
                  borderColor: 'var(--cyan)'
                }}
              >
                📖 DIRECTORY
              </Link>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="pixel-btn px-2 py-2 text-xs"
                style={{
                  background: 'var(--purple)',
                  color: isDarkTheme ? '#000' : '#fff',
                  borderColor: 'var(--purple)'
                }}
                title={isDarkTheme ? "Light Mode" : "Dark Mode"}
              >
                {isDarkTheme ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
              </button>

              {/* Music Toggle */}
              <button
                onClick={toggleMusic}
                className={`pixel-btn px-2 py-2 text-xs ${isMusicPlaying ? 'music-playing' : ''}`}
                style={{
                  background: 'var(--pink)',
                  color: isDarkTheme ? '#000' : '#fff',
                  borderColor: 'var(--pink)'
                }}
                title={isMusicPlaying ? "Mute" : "Play Music"}
              >
                {isMusicPlaying ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
              </button>
            </div>

            {/* Right: Compact Stat Boxes */}
            <div className="flex items-center gap-3">
              {/* Level Box */}
              <div 
                className="border-2 px-3 py-2 flex items-center gap-2"
                style={{
                  background: 'var(--bg)',
                  borderColor: 'var(--yellow)',
                  boxShadow: '0 0 10px rgba(250, 204, 21, 0.2)'
                }}
              >
                <Trophy className="w-4 h-4" style={{ color: 'var(--yellow)' }} />
                <div>
                  <div className="pixel-text text-xs leading-none" style={{ color: 'var(--gray)' }}>LEVEL</div>
                  <div className="pixel-text text-xl leading-tight" style={{ color: 'var(--yellow)' }}>{level}</div>
                </div>
              </div>

              {/* Points Box */}
              <div 
                className="border-2 px-3 py-2 flex items-center gap-2"
                style={{
                  background: 'var(--bg)',
                  borderColor: 'var(--cyan)',
                  boxShadow: '0 0 10px rgba(34, 211, 238, 0.2)'
                }}
              >
                <Star className="w-4 h-4" style={{ color: 'var(--cyan)' }} />
                <div>
                  <div className="pixel-text text-xs leading-none" style={{ color: 'var(--gray)' }}>POINTS</div>
                  <div className="pixel-text text-xl leading-tight" style={{ color: 'var(--cyan)' }}>{xp}</div>
                </div>
              </div>

              {/* Cooked Box */}
              <div 
                className="border-2 px-3 py-2 flex items-center gap-2"
                style={{
                  background: 'var(--bg)',
                  borderColor: 'var(--pink)',
                  boxShadow: '0 0 10px rgba(244, 114, 182, 0.2)'
                }}
              >
                <Utensils className="w-4 h-4" style={{ color: 'var(--pink)' }} />
                <div>
                  <div className="pixel-text text-xs leading-none" style={{ color: 'var(--gray)' }}>COOKED</div>
                  <div className="pixel-text text-xl leading-tight" style={{ color: 'var(--pink)' }}>{recipesCooked}</div>
                </div>
              </div>
            </div>
          </div>

          {/* XP Progress Bar - Full Width Below */}
          <div className="mt-3">
            <div className="flex justify-between items-center mb-1">
              <span className="pixel-text text-xs" style={{ color: 'var(--gray)' }}>
                EXP TO NEXT LEVEL
              </span>
              <span className="pixel-text text-xs" style={{ color: 'var(--green)' }}>
                {xpInLevel}/100
              </span>
            </div>
            <div 
              className="h-4 border-2 relative overflow-hidden"
              style={{ 
                background: 'var(--surface)', 
                borderColor: 'var(--green)'
              }}
            >
              <div 
                className="h-full transition-all duration-300" 
                style={{ 
                  width: `${xpInLevel}%`,
                  background: 'var(--green)',
                  boxShadow: '0 0 10px rgba(74, 222, 128, 0.5)'
                }}
              />
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <Routes>
        <Route path="/" element={<Game />} />
        <Route path="/directory" element={<RecipeDirectory />} />
      </Routes>
    </div>
  );
}

export default App;
